// SITE MATERIALS for the unified model and the timelapse: the building as it
// stands on site, not as the engineer's X-ray.
//
// The Studio's «Realist» layer (`realist3d.ts`) draws the concrete translucent
// so the rebar inside can be read — a verification drawing. The timelapse shows
// the site week by week, and on site the concrete is opaque, the rebar
// disappears into it once poured (the starter bars above the pour stay out),
// and masonry is a wall of blocks, not a 16 % veil (Stefan, 3.11.2026). Both
// drawings share the same meshes: the structure layer swaps materials
// (`construiesteStructura(...).aplicaBeton`).
//
// The textures are procedural (a canvas, generated once per page, a few tens
// of milliseconds), drawn in METRES and sampled TRIPLANAR — by world position,
// not by UV — so a box, an extruded outline and a clipped wall all get the
// same metre-true pattern, with no seams at the corners. Without a 2D canvas
// (jsdom in the tests) the materials fall back to a flat colour.
import * as THREE from "three";

/** The «Concrete» layer: solid (the site), translucent (the Studio's X-ray),
 *  hidden (only the rebar, the formwork and the masonry remain). */
export type ConcreteState = "solid" | "translucent" | "hidden";
export const CONCRETE_STATES: readonly ConcreteState[] = ["solid", "translucent", "hidden"];

export type MasonryKind = "clay" | "aac" | "plasterboard";

/** The masonry kind of a wall of the structure model. Imported projects carry
 *  the importer's material (`arhimport.materiale`: zidarie / bca / gips_carton);
 *  generated ones carry none, so the thickness decides — the same classes the
 *  bill of quantities prices (`A.ZID.30/25/20` clay blocks, `A.ZID.15/10` AAC). */
export function masonryKindOf(w: { material?: string | null; t_m?: number | null }): MasonryKind {
  if (w.material === "gips_carton") return "plasterboard";
  if (w.material === "bca") return "aac";
  if (w.material === "zidarie") return "clay";
  return (w.t_m ?? 0) >= 0.2 - 1e-6 ? "clay" : "aac";
}

// --- procedural textures, in metres --------------------------------------------

/** Smooth value noise on a wrapping grid: cheap, tileable, enough for grain. */
function noise(w: number, h: number, cell: number, seed: number): Float32Array {
  const gx = Math.max(1, Math.ceil(w / cell)), gy = Math.max(1, Math.ceil(h / cell));
  const rnd = lcg(seed);
  const g = new Float32Array(gx * gy);
  for (let i = 0; i < g.length; i++) g[i] = rnd();
  const at = (x: number, y: number) => g[(((y % gy) + gy) % gy) * gx + (((x % gx) + gx) % gx)];
  const out = new Float32Array(w * h);
  for (let y = 0; y < h; y++) {
    const fy = y / cell, y0 = Math.floor(fy), ty = fy - y0, sy = ty * ty * (3 - 2 * ty);
    for (let x = 0; x < w; x++) {
      const fx = x / cell, x0 = Math.floor(fx), tx = fx - x0, sx = tx * tx * (3 - 2 * tx);
      const a = at(x0, y0), b = at(x0 + 1, y0), c = at(x0, y0 + 1), d = at(x0 + 1, y0 + 1);
      out[y * w + x] = (a * (1 - sx) + b * sx) * (1 - sy) + (c * (1 - sx) + d * sx) * sy;
    }
  }
  return out;
}
function lcg(seed: number) {
  let s = seed >>> 0;
  return () => { s = (Math.imul(s, 1664525) + 1013904223) >>> 0; return s / 4294967296; };
}
function canvas2d(w: number, h: number): [HTMLCanvasElement, CanvasRenderingContext2D] | null {
  if (typeof document === "undefined" || /jsdom/i.test(navigator.userAgent)) return null;
  const c = document.createElement("canvas"); c.width = w; c.height = h;
  const ctx = c.getContext("2d");
  return ctx ? [c, ctx] : null;
}
function texture(c: HTMLCanvasElement): THREE.CanvasTexture {
  const t = new THREE.CanvasTexture(c);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.colorSpace = THREE.SRGBColorSpace;
  t.anisotropy = 8;
  return t;
}

/** Concrete, a 4 × 4 m tile at 256 px/m. Freshly stripped concrete is a neutral
 *  light grey with soft moisture patches and small pores; the «formed» variant
 *  adds the joints of the formwork panels (1,0 × 2,0 m framed panels) and the
 *  tie-rod holes; «smooth» is the power-floated top of a slab or a raft. */
function concreteTexture(formed: boolean): THREE.CanvasTexture | null {
  const W = 1024, M = 256;
  const cc = canvas2d(W, W);
  if (!cc) return null;
  const [c, ctx] = cc;
  const img = ctx.createImageData(W, W);
  const n1 = noise(W, W, 8, 11), n2 = noise(W, W, 48, 23), n3 = noise(W, W, 256, 37);
  const base = formed ? [166, 165, 161] : [176, 175, 171];
  for (let i = 0; i < W * W; i++) {
    const v = (n1[i] - 0.5) * 12 + (n2[i] - 0.5) * 16 + (n3[i] - 0.5) * 22;
    const cool = (n3[i] - 0.5) * 5;
    img.data[i * 4] = base[0] + v - cool; img.data[i * 4 + 1] = base[1] + v; img.data[i * 4 + 2] = base[2] + v + cool; img.data[i * 4 + 3] = 255;
  }
  ctx.putImageData(img, 0, 0);
  const rnd = lcg(7);
  ctx.fillStyle = "rgba(58,56,53,0.32)";
  for (let k = 0; k < (formed ? 2400 : 1400); k++) { ctx.beginPath(); ctx.arc(rnd() * W, rnd() * W, 0.6 + rnd() * 1.5, 0, 6.283); ctx.fill(); }
  if (formed) {
    for (let x = 0; x <= W; x += M) { ctx.fillStyle = "rgba(66,64,60,0.5)"; ctx.fillRect(x - 1, 0, 2, W); ctx.fillStyle = "rgba(255,255,255,0.09)"; ctx.fillRect(x + 1, 0, 2, W); }
    for (let y = 0; y <= W; y += 2 * M) { ctx.fillStyle = "rgba(66,64,60,0.5)"; ctx.fillRect(0, y - 1, W, 2); ctx.fillStyle = "rgba(255,255,255,0.09)"; ctx.fillRect(0, y + 1, W, 2); }
    for (let x = 0; x < W; x += M) for (let y = 0; y < W; y += 2 * M) for (const [dx, dy] of [[0.25, 0.3], [0.75, 0.3], [0.25, 1.7], [0.75, 1.7]]) {
      const px = x + dx * M, py = y + dy * M;
      ctx.fillStyle = "rgba(118,116,112,0.9)"; ctx.beginPath(); ctx.arc(px, py, 5, 0, 6.283); ctx.fill();
      ctx.fillStyle = "rgba(52,50,48,0.95)"; ctx.beginPath(); ctx.arc(px, py, 3.2, 0, 6.283); ctx.fill();
    }
  }
  return texture(c);
}

/** Masonry as blocks, drawn with the first course at the bottom of the canvas
 *  (v = 0): clay blocks 37,5 × 23,8 cm on a 12 mm mortar bed, or AAC blocks
 *  60 × 25 cm on a 2 mm thin-bed adhesive; half-block stagger. The relief (a
 *  shadow under the bed joint, a highlight on the top edge) is baked into the
 *  colour: at model scale a bump map would not read. */
function masonryTexture(kind: "clay" | "aac"): { tex: THREE.CanvasTexture; tile: [number, number] } | null {
  const M = 512;
  const block = kind === "clay" ? [0.375, 0.238] : [0.6, 0.25];
  const joint = kind === "clay" ? [0.004, 0.012] : [0.002, 0.002];
  const stepX = block[0] + joint[0], stepY = block[1] + joint[1];
  const nx = kind === "clay" ? 4 : 2, ny = 4;
  const Wm = nx * stepX, Hm = ny * stepY;
  const W = Math.round(Wm * M), H = Math.round(Hm * M);
  const cc = canvas2d(W, H);
  if (!cc) return null;
  const [c, ctx] = cc;
  ctx.fillStyle = kind === "clay" ? "#8d8881" : "#b5b3ad"; ctx.fillRect(0, 0, W, H);
  const rnd = lcg(3);
  const base = kind === "clay" ? [186, 104, 68] : [213, 212, 205];
  for (let j = 0; j < ny; j++) {
    const y0 = H - (j + 1) * stepY * M + joint[1] * M;
    const shift = (j % 2) * stepX / 2;
    for (let i = -1; i <= nx; i++) {
      const x0 = (i * stepX + shift) * M;
      const k = (rnd() - 0.5) * (kind === "clay" ? 0.18 : 0.06);
      const col = base.map((v) => Math.round(v * (1 + k)));
      ctx.fillStyle = `rgb(${col[0]},${col[1]},${col[2]})`;
      ctx.fillRect(x0, y0, block[0] * M, block[1] * M);
      ctx.fillStyle = "rgba(0,0,0,0.16)"; ctx.fillRect(x0, y0, block[0] * M, 2); ctx.fillRect(x0, y0, 2, block[1] * M);
      ctx.fillStyle = "rgba(255,255,255,0.10)"; ctx.fillRect(x0, y0 + block[1] * M - 2, block[0] * M, 2);
    }
  }
  const img = ctx.getImageData(0, 0, W, H); const n = noise(W, H, 3, 5);
  for (let i = 0; i < W * H; i++) { const v = (n[i] - 0.5) * (kind === "clay" ? 20 : 12); img.data[i * 4] += v; img.data[i * 4 + 1] += v; img.data[i * 4 + 2] += v; }
  ctx.putImageData(img, 0, 0);
  return { tex: texture(c), tile: [Wm, Hm] };
}

type Textures = {
  formed: THREE.Texture | null; smooth: THREE.Texture | null;
  clay: { tex: THREE.Texture; tile: [number, number] } | null;
  aac: { tex: THREE.Texture; tile: [number, number] } | null;
};
let textures: Textures | undefined;
/** Built once per page, on first use; never disposed (five small textures). */
function siteTextures(): Textures {
  if (!textures) textures = { formed: concreteTexture(true), smooth: concreteTexture(false), clay: masonryTexture("clay"), aac: masonryTexture("aac") };
  return textures;
}

// --- triplanar materials ------------------------------------------------------

type Triplanar = { vert: THREE.Texture; horiz: THREE.Texture; tileV: [number, number]; tileH: [number, number] };
/** The triplanar setup of a material, kept beside it (not in `userData`, which
 *  `Material.copy` serialises to JSON) so a clone can be given the same shader. */
const setups = new WeakMap<THREE.Material, Triplanar>();

/** Samples `vert` on the vertical faces and `horiz` on the horizontal ones, by
 *  world position; the blend weights come from the world normal, sharpened so
 *  a box face takes one texture and the corners don't smear. */
function applyTriplanar(m: THREE.MeshStandardMaterial, s: Triplanar) {
  setups.set(m, s);
  m.map = s.vert;
  m.onBeforeCompile = (sh) => {
    sh.uniforms.tHoriz = { value: s.horiz };
    sh.uniforms.uRepV = { value: new THREE.Vector2(1 / s.tileV[0], 1 / s.tileV[1]) };
    sh.uniforms.uRepH = { value: new THREE.Vector2(1 / s.tileH[0], 1 / s.tileH[1]) };
    sh.vertexShader = sh.vertexShader
      .replace("#include <common>", "#include <common>\nvarying vec3 vTriPos; varying vec3 vTriNrm;")
      .replace("#include <worldpos_vertex>", "#include <worldpos_vertex>\nvTriPos = (modelMatrix * vec4(transformed, 1.0)).xyz; vTriNrm = normalize(mat3(modelMatrix) * objectNormal);");
    sh.fragmentShader = sh.fragmentShader
      .replace("#include <common>", "#include <common>\nvarying vec3 vTriPos; varying vec3 vTriNrm; uniform sampler2D tHoriz; uniform vec2 uRepV; uniform vec2 uRepH;")
      .replace("#include <map_fragment>", [
        "vec3 bw = abs(normalize(vTriNrm)); bw = pow(bw, vec3(8.0)); bw /= (bw.x + bw.y + bw.z);",
        "vec4 cX = texture2D(map, vTriPos.yz * uRepV);",
        "vec4 cY = texture2D(map, vTriPos.xz * uRepV);",
        "vec4 cZ = texture2D(tHoriz, vTriPos.xy * uRepH);",
        "diffuseColor *= cX * bw.x + cY * bw.y + cZ * bw.z;",
      ].join("\n"));
  };
  m.customProgramCacheKey = () => `triplanar-${s.tileV.join("x")}-${s.tileH.join("x")}`;
}

/** Opaque concrete: «formed» has the formwork marks on its vertical faces and a
 *  floated top (columns, beams, walls, slabs, footings); «smooth» is floated
 *  all over (the raft, the slab on grade). Flat grey without a canvas. */
export function concreteMaterial(finish: "formed" | "smooth"): THREE.MeshStandardMaterial {
  const m = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.9, metalness: 0, envMapIntensity: 1.0 });
  const t = siteTextures();
  const vert = finish === "formed" ? t.formed : t.smooth;
  if (vert && t.smooth) applyTriplanar(m, { vert, horiz: t.smooth, tileV: [4, 4], tileH: [4, 4] });
  else m.color.setRGB(166 / 255, 165 / 255, 161 / 255);
  return m;
}

/** Masonry blocks in real size; plasterboard is a flat light grey. */
export function masonryMaterial(kind: MasonryKind): THREE.MeshStandardMaterial {
  if (kind === "plasterboard") return new THREE.MeshStandardMaterial({ color: 0xe6e4df, roughness: 0.95, metalness: 0, envMapIntensity: 0.5 });
  const m = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.95, metalness: 0, envMapIntensity: 0.55 });
  const t = siteTextures()[kind];
  if (t) applyTriplanar(m, { vert: t.tex, horiz: t.tex, tileV: t.tile, tileH: t.tile });
  else m.color.setHex(kind === "clay" ? 0xba6844 : 0xd5d4cd);
  return m;
}

/** A copy of `m` cut by `plane` (a wall rising course by course in the
 *  timelapse): same shader, both faces drawn so the cut doesn't look hollow,
 *  shadow cut with it. `Material.clone` drops `onBeforeCompile`, hence the
 *  re-application. */
export function withClipPlane(m: THREE.MeshStandardMaterial, plane: THREE.Plane): THREE.MeshStandardMaterial {
  const c = m.clone();
  const s = setups.get(m);
  if (s) applyTriplanar(c, s);
  c.clippingPlanes = [plane];
  c.clipShadows = true;
  c.side = THREE.DoubleSide;
  return c;
}
